/*
 * Появление блоков при открытии страницы и при скролле.
 *
 * Логика вынесена из директив `v-reveal` / `v-reveal-group`
 * (front/plugins/v-reveal.js), чтобы её можно было покрыть тестами.
 *
 * Состояния задаются инлайн-стилями: страницы кейсов используют scoped-CSS,
 * селекторы которого специфичнее глобальных классов, поэтому класс-состояние
 * не перебил бы собственные `transition`/`transform` страниц.
 * После анимации инлайн-стили снимаются — элемент живёт по CSS страницы.
 */

/* Насколько блок смещён вниз до появления, px */
export const REVEAL_OFFSET_PX = 24

/* Длительность проявления, мс */
export const REVEAL_DURATION_MS = 600

/* Задержка между блоками одной группы, мс */
export const REVEAL_STEP_MS = 90

/* Ограничение каскада: дальше пятого шага блоки появляются одновременно */
export const REVEAL_MAX_STEPS = 5

/* Блок появляется, когда его верхняя граница поднимается выше 97% окна */
export const REVEAL_ROOT_MARGIN = '0px 0px -3% 0px'

const REVEAL_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)'

/* Запас после окончания анимации: страхует, если transitionend не пришёл */
const REVEAL_CLEANUP_EXTRA_MS = 60

const REVEAL_INLINE_PROPERTIES = [
  'opacity',
  'transform',
  'transition',
  'transitionDelay',
]

/*
 * Анимируем только в браузере и только если пользователь не просил уменьшить
 * движение (accessibility). Иначе блоки остаются в обычном виде.
 */
export const isRevealAllowed = () => {
  if (
    typeof window === 'undefined' ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return false
  }

  if (typeof window.matchMedia !== 'function') {
    return true
  }

  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/*
 * Блок внутри другого блока (например h2 внутри head) отдельно не анимируем:
 * иначе две анимации наложатся друг на друга.
 */
export const filterNestedTargets = (targets) =>
  targets.filter(
    (target) =>
      !targets.some((other) => other !== target && other.contains(target))
  )

/*
 * Ищем блоки внутри root. Селекторов может быть несколько (строкой или
 * массивом): querySelectorAll возвращает их в порядке документа, поэтому
 * каскад задержек совпадает с порядком чтения.
 */
export const collectRevealTargets = (root, selector) => {
  const query = Array.isArray(selector) ? selector.join(', ') : selector

  return filterNestedTargets(Array.from(root.querySelectorAll(query)))
}

const hideTarget = (target) => {
  target.style.opacity = '0'
  target.style.transform = `translateY(${REVEAL_OFFSET_PX}px)`
}

const showTarget = (target, delayMs) => {
  const transition = [
    `opacity ${REVEAL_DURATION_MS}ms ${REVEAL_EASING}`,
    `transform ${REVEAL_DURATION_MS}ms ${REVEAL_EASING}`,
  ].join(', ')

  target.style.transition = transition
  target.style.transitionDelay = `${delayMs}ms`
  target.style.opacity = '1'
  target.style.transform = 'translateY(0)'
}

/* Снимаем инлайн-стили, чтобы элемент вернулся к CSS страницы */
const resetTarget = (target) => {
  REVEAL_INLINE_PROPERTIES.forEach((property) => {
    target.style[property] = ''
  })
}

const onNextFrame = (callback) =>
  typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame(callback)
    : setTimeout(callback, 0)

const cancelFrame = (frame) => {
  if (typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(frame)

    return
  }

  clearTimeout(frame)
}

/*
 * Прячем блоки и показываем их, когда они попадают в область видимости.
 * Блоки, попавшие в кадр одной пачкой, появляются каскадом.
 * Возвращаем контроллер — директива вызывает его при удалении элемента.
 */
export const createReveal = (targets, { delay = 0 } = {}) => {
  const timers = []
  let observer = null

  const showEntry = (entry, batchIndex) => {
    const delayMs =
      delay + Math.min(batchIndex, REVEAL_MAX_STEPS) * REVEAL_STEP_MS

    observer.unobserve(entry.target)
    showTarget(entry.target, delayMs)

    timers.push(
      setTimeout(
        () => resetTarget(entry.target),
        delayMs + REVEAL_DURATION_MS + REVEAL_CLEANUP_EXTRA_MS
      )
    )
  }

  observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .forEach((entry, batchIndex) => showEntry(entry, batchIndex))
    },
    { rootMargin: REVEAL_ROOT_MARGIN }
  )

  targets.forEach((target) => hideTarget(target))

  /*
   * Наблюдаем со следующего кадра: к этому моменту скрытое состояние уже
   * отрисовано, и переход успевает сработать.
   */
  const frame = onNextFrame(() => {
    targets.forEach((target) => observer.observe(target))
  })

  return {
    disconnect() {
      observer.disconnect()
      timers.forEach((timer) => clearTimeout(timer))
      timers.length = 0

      cancelFrame(frame)
    },
  }
}
